import { useQuery } from '@tanstack/react-query';
import { Input, Table, Image } from 'antd';
import type { TableProps } from 'antd/es/table';
import type {
  ColumnsType,
  ColumnType,
  FilterValue
} from 'antd/es/table/interface';
import { useMemo, useRef, useState } from 'react';
import { Category } from '../../../models/Category/Category';
import { ButtonPrimary } from '../../components/Button/ButtonPrimary';
import style from './category-list.module.scss';
import { Link, useNavigate } from 'react-router-dom';
import { FaEdit as EditIcon, FaTrash as DeleteIcon } from 'react-icons/fa';
import { SearchOutlined } from '@ant-design/icons';
import axiosAPI from '../../../utils/client/axiosAPI';
import { toast } from 'react-toastify';
import ConfirmationModal from '../../components/Modals/ConfirmationModal/ConfirmationModal';

function countCategories(categories: Category[]): number {
  let count = 0;
  for (const cat of categories) {
    count += 1;
    if (cat.children && cat.children.length > 0) {
      count += countCategories(cat.children);
    }
  }
  return count;
}
type CategoryTableList = {
  id: number;
  name: string;
  price: number;
  cover?: string;
  productsCount: number;
  level?: number;
  parent_id: number | null;
};

type DataIndex = keyof CategoryTableList;

export default function CategoryList() {
  const searchInput = useRef<any>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { data: categories = [], refetch } = useQuery(['categories'], () =>
    Category.all('product')
  );
  const [filteredInfo, setFilteredInfo] = useState<
    Record<string, FilterValue | null>
  >({});
  const navigate = useNavigate();

  function flattenCategories(
    categories: Category[],
    level = 0
  ): CategoryTableList[] {
    return categories.flatMap((cat) => [
      {
        id: cat.id,
        name: cat.name,
        price: cat.price,
        productsCount: cat.productsCount ?? 0,
        parent_id: cat.parentId,
        level
      },
      ...(cat.children?.length
        ? flattenCategories(cat.children, level + 1)
        : [])
    ]);
  }

  // In your component:
  const CategoryData: CategoryTableList[] = useMemo(
    () => flattenCategories(categories ?? []),
    [categories]
  );

  // Search ONLY for Naziv (name)
  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): ColumnType<CategoryTableList> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters
    }) => (
      <div className="cs-table-dropdown" onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Pretraži ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => confirm()}
        />
        <div className="cs-table-dropdown-actions">
          <button onClick={() => confirm()} className="cs-table-button-primary">
            Pretraži
          </button>
          <button
            onClick={() => clearFilters && clearFilters()}
            className="cs-table-button-secondary"
          >
            Resetuj
          </button>
        </div>
      </div>
    ),
    filterIcon: (filtered: boolean) => <SearchOutlined />,
    filteredValue: filteredInfo[dataIndex] || null,
    onFilter: (value, record) =>
      !!record[dataIndex] &&
      record[dataIndex]!.toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) setTimeout(() => searchInput.current?.select(), 100);
    }
  });

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await Category.delete(deleteTarget.id);
      toast.success('Kategorija uspešno obrisana.');
      setShowDeleteModal(false);
      setDeleteTarget(null);
      refetch(); // Refresh table
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || 'Greška pri brisanju kategorije.'
      );
    } finally {
      setDeleting(false);
    }
  };

  const columns: ColumnsType<CategoryTableList> = [
    {
      title: 'Naziv',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSearchProps('name'),
      render: (name, row) => (
        <div
          className="cs-table-link"
          style={{
            paddingLeft: `${(row.level ?? 0) * 18}px`,
            textAlign: 'left'
          }}
          onClick={() => navigate(`/admin/categories/edit/${row.id}`)}
        >
          {name}
        </div>
      )
    },
    {
      title: 'Cena',
      dataIndex: 'price',
      key: 'price',
      sorter: (a, b) => Number(a.price) - Number(b.price),
      render: (price) => <span>{price} RSD</span>
    },
    {
      title: 'Broj proizvoda',
      dataIndex: 'productsCount',
      key: 'productsCount',
      sorter: (a, b) => (a.productsCount ?? 0) - (b.productsCount ?? 0),
      render: (productsCount) => <span>{productsCount ?? 0}</span>
    },
    {
      key: 'action',
      title: 'Akcije',
      render: (_, category) => (
        <div className={style.actions}>
          <Link
            className={style.editButton}
            to={`/admin/categories/edit/${category.id}`}
          >
            <EditIcon />
          </Link>
          {/* Only allow delete for non-top-level categories */}
          {category.parent_id !== null && (
            <button
              className={style.deleteButton}
              onClick={() => {
                setDeleteTarget({ id: category.id, name: category.name });
                setShowDeleteModal(true);
              }}
            >
              <DeleteIcon />
            </button>
          )}
        </div>
      )
    }
  ];

  const handleChange: TableProps<CategoryTableList>['onChange'] = (
    pagination,
    filters,
    sorter
  ) => {
    setFilteredInfo(filters);
  };

  return (
    <div className="cs-inner-container">
      <h1 className="cs-title">Lista Kategorija</h1>
      <div className={style.info}>
        <div className={style.number}>
          Trenutno imate <strong>{countCategories(categories ?? [])}</strong>{' '}
          kategorija
        </div>
        <ButtonPrimary url="/admin/categories/create">
          Dodaj Kategoriju
        </ButtonPrimary>
      </div>
      <Table
        columns={columns}
        dataSource={CategoryData}
        rowKey="id"
        onChange={handleChange}
        className="cs-table-wrapper"
        pagination={false}
      />
      <ConfirmationModal
        show={showDeleteModal}
        title="Potvrda brisanja"
        message={
          deleteTarget
            ? `Da li ste sigurni da želite da obrišete kategoriju "${deleteTarget.name}"? Svi proizvodi iz ove kategorije biće premešteni u roditeljsku kategoriju.`
            : ''
        }
        confirmLabel={deleting ? 'Brišem...' : 'Obriši'}
        cancelLabel="Otkaži"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
      />
    </div>
  );
}
